import { inject, Injectable } from '@angular/core';
import { AccessRightFilterCollectionModel } from '@fi-shared/models';
import { FiContentLanguageSharedService } from '@fi-shared/fi-content-language-shared/fi-content-language-shared.service';
import {
    IQuizEditItem,
    IQuizNotification,
    IQuizQuestion,
    IQuizQuestionPresetOption,
} from '@fi-app/fi-onboarding/fi-quiz/fi-quiz';
import { FiQuizNotificationRecipientType, FiQuizQuestionType } from '@fi-app/fi-onboarding/fi-quiz/fi-quiz-enums';
import { FiQuizEditorStoreService } from './fi-quiz-edit/fi-quiz-editor-store.service';
import { IMediaBankFileReference } from '@fi-app/shared/media-bank';
import { IContentLanguageLabelModel } from '@fi-shared/fi-content-language-shared/fi-content-language-shared';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { FiQuizNotificationDialogComponent } from './fi-quiz-edit/quiz-settings-tab/notifications-settings/quiz-notification-dialog/quiz-notification-dialog.component';
import { FiDialogsService } from '@fi-app/shared/fi-dialogs/fi-dialogs.service';
import { IFiDialogResult } from '@fi-app/shared/fi-dialogs/fi-dialogs.d';
import { isDefined } from '@fi-app/shared/utils/is-defined';
import { AppTextService } from '@fi-app/shared/app-text';
import { NgModel } from '@angular/forms';
import { isNullOrUndefined } from '@fi-app/shared/utils/is-null-or-undefined';
import { FiQuizEditorTabIndex } from './fi-quiz-enums';
import { FiItemSharingSettingsDialogService } from '@fi-app/shared/access-right-filters/fi-item-sharing-settings-dialog/fi-item-sharing-settings.service';
import { ObjectTypes } from '@fi-app/shared/types';
import { FiDeleteConnectedOboardingElementDialogService } from '@fi-app/fi-onboarding/fi-delete-connected-oboarding-element-dialog/fi-delete-connected-oboarding-element-dialog.service';
import { FiSnackBarService } from '@fi-shared/fi-snackbar/snack-bar.service';

@Injectable()
export class FiQuizHelperService {
    private fiQuizEditorStoreService = inject(FiQuizEditorStoreService);
    private fiContentLanguageService = inject(FiContentLanguageSharedService);
    private fiDialogsService = inject(FiDialogsService);
    private appTextService = inject(AppTextService);
    private fiItemSharingSettingsDialogService = inject(FiItemSharingSettingsDialogService);
    private fiDeleteConnectedOboardingElementDialogService = inject(FiDeleteConnectedOboardingElementDialogService);
    private fiSnackBarService = inject(FiSnackBarService);

    readonly quizQuestionsList = [
        {
            iconClass: 'far fa-circle-dot BrandedColor',
            labelId: 'forms.answer_single_choice',
            type: FiQuizQuestionType.SingleAnswer,
        },
        {
            iconClass: 'far fa-square-check BrandedColor',
            labelId: 'forms.answer_multiple_choice',
            type: FiQuizQuestionType.MultipleAnswers,
        },
    ];
    readonly quizAvailableQuestionTypesDropListId = 'quiz-available-question-types-drop-list';
    readonly quizAddedQuestionsDropListId = 'quiz-added-questions-drop-list';

    getQuizHeaderText(quizName: string, isCopyMode: boolean): string {
        if (isCopyMode) {
            return (
                this.appTextService.get('quiz.edit_quiz') +
                ': (' +
                this.appTextService.get('copy.noun') +
                ') ' +
                quizName
            );
        }
        if (isDefined(quizName)) {
            return this.appTextService.get('quiz.edit_quiz') + ': ' + quizName;
        }
        return this.appTextService.get('quiz.new_quiz');
    }

    initContentLanguages(quiz: IQuizEditItem): IQuizEditItem {
        this.fiContentLanguageService.initializeContentLanguagesArray(quiz.Names, quiz.Names);
        this.fiContentLanguageService.initializeContentLanguagesArray(quiz.Descriptions, quiz.Descriptions);
        quiz.Questions.forEach((question) => {
            this.fiContentLanguageService.initializeContentLanguagesArray(question.Names, question.Names);
        });
        return quiz;
    }

    insertCopyLabelIntoContentLanguageLabelModels(
        contentLanguageLabelModels: IContentLanguageLabelModel[],
    ): IContentLanguageLabelModel[] {
        return this.fiContentLanguageService.insertCopyLabelIntoContentLanguageLabelModels(contentLanguageLabelModels);
    }

    getNewQuizInstance(): IQuizEditItem {
        return {
            Names: this.fiContentLanguageService.getContentLanguageLabelModels(),
            Descriptions: this.fiContentLanguageService.getContentLanguageLabelModels(),
            IsShared: true,
            PassThreshold: 100,
            AllowRetakes: false,
            IsTimerEnabled: false,
            TimerMs: 0,
            AccessRightFilterCollection: AccessRightFilterCollectionModel.getNewInstance(),
            MediaBankFileReferences: [],
            Questions: [],
            Notifications: [],
        };
    }

    getNewQuizNotificationInstance(): IQuizNotification {
        return {
            RecipientType: FiQuizNotificationRecipientType.RolesLocal,
            ViaNotification: true,
            ViaSMS: false,
            ViaEmail: false,
            UserIds: [],
            RoleIds: [],
            UnitIds: [],
            UserContacts: [],
        };
    }

    getNewQuizQuestionInstance(questionType: FiQuizQuestionType): IQuizQuestion {
        return {
            Names: this.fiContentLanguageService.getContentLanguageLabelModels(),
            Type: questionType,
            PresetOptions: [
                this.getNewQuizQuestionPresetOptionInstance(),
                this.getNewQuizQuestionPresetOptionInstance(),
            ],
        };
    }

    getNewQuizQuestionPresetOptionInstance(): IQuizQuestionPresetOption {
        return {
            Names: this.fiContentLanguageService.getContentLanguageLabelModels(),
            IsCorrect: false,
        };
    }

    addNewQuizQuestion(questionType: FiQuizQuestionType): void {
        const newQuestion = this.getNewQuizQuestionInstance(questionType);
        this.fiQuizEditorStoreService.updateCurrentQuizData((current) => {
            return {
                ...current,
                Questions: [...current.Questions, newQuestion],
            };
        });
        this.fiQuizEditorStoreService.setScrollToQuestionCard(this.fiQuizEditorStoreService.currentQuizData().Questions.length - 1);
    }

    removeQuizQuestionByIndex(questionIndex: number): void {
        this.fiQuizEditorStoreService.updateCurrentQuizData((current) => {
            return {
                ...current,
                Questions: current.Questions.filter((_, i) => i !== questionIndex),
            };
        });
    }

    onQuizNamesChange(names: IContentLanguageLabelModel[]): void {
        this.fiQuizEditorStoreService.updateCurrentQuizData((current) => {
            return {
                ...current,
                Names: names,
            };
        });
    }

    onQuizDescriptionsChange(descriptions: IContentLanguageLabelModel[]): void {
        this.fiQuizEditorStoreService.updateCurrentQuizData((current) => {
            return {
                ...current,
                Descriptions: descriptions,
            };
        });
    }

    onQuizMediaBankFileReferencesChange(mediaBankFileReferences: IMediaBankFileReference[]): void {
        this.fiQuizEditorStoreService.updateCurrentQuizData((current) => {
            return {
                ...current,
                MediaBankFileReferences: mediaBankFileReferences,
            };
        });
    }

    insertNewQuizQuestionAtPosition(questionType: FiQuizQuestionType, index: number): void {
        const newQuestion = this.getNewQuizQuestionInstance(questionType);
        this.fiQuizEditorStoreService.updateCurrentQuizData((current) => {
            return {
                ...current,
                Questions: [...current.Questions.slice(0, index), newQuestion, ...current.Questions.slice(index)],
            };
        });
    }

    onQuizQuestionsPositionChange(previousIndex: number, currentIndex: number): void {
        const questions = this.fiQuizEditorStoreService.currentQuizData().Questions;
        moveItemInArray(questions, previousIndex, currentIndex);
        this.fiQuizEditorStoreService.updateCurrentQuizData((current) => {
            return {
                ...current,
                Questions: questions,
            };
        });
    }

    addQuizQuestionPresetOption(questionIndex: number): void {
        const newPresetOption = this.getNewQuizQuestionPresetOptionInstance();
        this.fiQuizEditorStoreService.updateCurrentQuizData((current) => {
            return {
                ...current,
                Questions: current.Questions.map((question, index) => {
                    return index === questionIndex
                        ? { ...question, PresetOptions: [...question.PresetOptions, newPresetOption] }
                        : question;
                }),
            };
        });
    }

    deleteQuizQuestionPresetOption(questionIndex: number, presetOptionIndex: number): void {
        this.fiQuizEditorStoreService.updateCurrentQuizData((current) => {
            return {
                ...current,
                Questions: current.Questions.map((question, index) => {
                    return index === questionIndex
                        ? {
                              ...question,
                              PresetOptions: question.PresetOptions.filter((_, index) => index !== presetOptionIndex),
                          }
                        : question;
                }),
            };
        });
    }

    confirmDeleteQuizQuestion(question: IQuizQuestion): void {
        this.fiDeleteConnectedOboardingElementDialogService
            .openDialog({
                isNewElement: !isDefined(question.Id),
                ObjectType: ObjectTypes.QuizQuestion,
                ConnectedOnboardings: this.fiQuizEditorStoreService.quizConnectedOnboardings(),
                HasResponses: question.HasResponses,
            })
            .onYesButtonClick(() => this.removeQuestionFromQuiz(question));
    }

    private removeQuestionFromQuiz(question: IQuizQuestion): void {
        const questionIndex = this.fiQuizEditorStoreService.currentQuizData().Questions.indexOf(question);
        this.removeQuizQuestionByIndex(questionIndex);
    }

    openQuizNotificationEditorDialog(notification?: IQuizNotification): IFiDialogResult<IQuizNotification> {
        if (!isDefined(notification)) {
            notification = this.getNewQuizNotificationInstance();
        }
        return this.fiDialogsService.custom({
            contentComponentRef: FiQuizNotificationDialogComponent,
            contentComponentData: notification,
            hideDialogTopBorder: true,
            headerText: this.appTextService.get('quiz.settings_notifications_header'),
            preventUppercaseButtonLabel: true,
            isHeaderDividerShown: true,
            headerDividerClass: 'BrandedBorderColor',
            yesButtonText: this.appTextService.get('done'),
            noButtonText: this.appTextService.get('common.cancel'),
            config: {
                panelClass: [
                    'fi-rounded-dialog-panel',
                    'fi-dialog-content-no-bottom-padding',
                    'fi-quiz-notification-dialog-panel',
                    'fi-rounded-dialog-full-round-buttons',
                ],
                autoFocus: false,
                width: '850px',
                maxWidth: '80%',
            },
        });
    }

    private quizEditorModelsNames = ['quiz-added-questions-list', 'Name', 'question-preset-options', 'question-card'];
    handleQuizEditFormError(invalidModels: NgModel[]): void {
        this.showMissingMandatoryInfoDialog();
        this.fiQuizEditorStoreService.setFormFailedValidation(true);

        const tabIndex = this.isSectionInvalid(invalidModels, this.quizEditorModelsNames)
            ? FiQuizEditorTabIndex.QuizEditor
            : FiQuizEditorTabIndex.SettingsSharing;
        this.fiQuizEditorStoreService.setSelectedTabIndex(tabIndex);
        if (this.isMissingCorrectOption(invalidModels)) {
            this.fiSnackBarService.showSnackbar({
                textMessage: this.appTextService.get('quiz.error_banner_text'),
                buttonIconClass: 'far fa-xmark',
                durationMs: 5000,
                colorClass: 'red',
                iconClass: 'far fa-info-circle',
            }, true, 'fi-snackbar-standard');
        }
    }

    private isSectionInvalid(invalidModels: NgModel[], sectionModelNames: string[]): boolean {
        const modelNames = invalidModels
            .map((model) => model.name)
            .filter((name) => !isNullOrUndefined(name) && !!name.trim().length);

        return sectionModelNames.some((editorModelName) => {
            return modelNames.some((modelName) => modelName.includes(editorModelName));
        });
    }

    private isMissingCorrectOption(invalidModels: NgModel[]): boolean {
        const questionCardsModels = invalidModels.filter((model) => model.name?.includes('question-card'));
        return questionCardsModels.some((model) => model.hasError('missing-correct-option'));
    }

    private showMissingMandatoryInfoDialog(): void {
        this.fiDialogsService.notify({
            messageText: this.appTextService.get('common.error_all_mandatory'),
            headerText: this.appTextService.get('error.missing_mandatory_info'),
            headerIconClass: 'far fa-exclamation-circle red',
            isBigSizeTopBorder: true,
            colorClass: 'red',
            yesButtonText: this.appTextService.get('ok'),
            config: {
                panelClass: 'fi-rounded-dialog-panel',
                autoFocus: false,
            },
        });
    }

    setInitialSavedQuickSelectionFilters(): void {
        const quizQuickSelectionFilters = this.fiQuizEditorStoreService.savedSelectionFilters();
        const quiz = this.fiQuizEditorStoreService.currentQuizData();

        const selectedFiltersCollection = this.fiItemSharingSettingsDialogService.getSavedFilterBySharingSelection(
            quiz.AccessRightFilterCollection.AccessRightCoreFilter,
            quizQuickSelectionFilters,
            true,
        );
        this.fiQuizEditorStoreService.setSelectedFiltersCollection(selectedFiltersCollection);
    }

    setInitialSelectedLanguageId(quiz: IQuizEditItem): void {
        const selectedLanguageId = this.fiContentLanguageService.getContentLanguageIdByFilledModels(quiz.Names);
        this.fiQuizEditorStoreService.setSelectedLanguageId(selectedLanguageId);
    }
}
